import { Flex, message, Popconfirm, Space, Tag } from "antd";
import { useAppDispatch } from "../../redux/hooks.ts";
import { Folder } from "../../redux/interfaces/folder.types.ts";
import { generateTagColors } from "../../utils/common/generateTagColors.ts";
import {
  fetchFolderById,
  onRemoveFolderTemplate,
  setActiveFolderAsync,
} from "../../redux/actions/folderActions.tsx";
import { removeSubfolderFromFolder } from "../../redux/actions/templatesActions.tsx";
import { getOwnerNameFromForms } from "../../utils/subfolders/ownerName.ts";
import { useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";

interface Props {
  folder: Folder;
}

type TemplateTagItem = {
  // Unique per tag — the subfolder id when available (so duplicate-template tags are
  // distinct), falling back to the slug for the legacy templates-only shape.
  key: string;
  subfolderId: string | null;
  templateId: string;
  name: string;
  slug: string;
  // Person's display name (from form values or metadata) — distinguishes duplicate templates.
  personName?: string;
  // How many owner representatives point at this subfolder (deleted in cascade with it).
  repCount: number;
};

export const TemplateList: React.FC<Props> = ({ folder }) => {
  const dispatch = useAppDispatch();
  const params = useParams();

  const [messageApi, contextHolder] = message.useMessage();

  const [openTemplatePopConfirm, setOpenTemplatePopConfirm] = useState<
    string | null
  >(null);

  const items: TemplateTagItem[] =
    folder.subfolders?.map((subfolder) => ({
      key: subfolder.id,
      subfolderId: subfolder.id,
      templateId: subfolder.template.id,
      name: subfolder.template.name,
      slug: subfolder.template.slug,
      personName:
        getOwnerNameFromForms(subfolder) ||
        subfolder.metadata?.full_name ||
        undefined,
      repCount: (folder.subfolders ?? []).filter(
        (sf) => sf.metadata?.represents_subfolder_id === subfolder.id,
      ).length,
    })) ||
    folder.templates?.map((template) => ({
      key: template.slug,
      subfolderId: null,
      templateId: template.id,
      name: template.name,
      slug: template.slug,
      repCount: 0,
    })) ||
    [];

  const template_ids = items.map((item) => item.templateId);

  const colors = generateTagColors(template_ids);

  const onActiveFolder = async () => {
    await dispatch(setActiveFolderAsync(folder));
  };

  const handleDelete = async (item: TemplateTagItem) => {
    try {
      if (item.subfolderId) {
        // Delete only this participant's subfolder, not every duplicate of the slug.
        await dispatch(
          removeSubfolderFromFolder({
            folderId: folder.id,
            subfolderId: item.subfolderId,
          }),
        ).unwrap();
      } else {
        await dispatch(
          onRemoveFolderTemplate({
            folder_id: folder.id,
            template_slug: item.slug,
          }),
        ).unwrap();
      }

      if (params.id) {
        const updated = await dispatch(fetchFolderById(params.id)).unwrap();
        await dispatch(setActiveFolderAsync(updated));
      }
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo eliminar la plantilla del expediente."),
      );
    }
  };

  return (
    <div className="w-6/12 my-4" onClick={onActiveFolder}>
      {contextHolder}
      <Flex gap="4px 0" wrap>
        {items.map((item) => (
          <Tag className="mb-2" key={item.key} color={colors[item.templateId]}>
            <Space className="w-full flex justify-center items-center">
              <span>
                {item.name}
                {item.personName ? ` · ${item.personName}` : ""}
              </span>
              <Popconfirm
                title={`¿Estás seguro de eliminar la plantilla ${item.name}${item.personName ? ` de ${item.personName}` : ""}?`}
                description={
                  <>
                    <p>
                      Los requisitos de la plantilla {item.name} serán
                      eliminados.
                    </p>
                    {item.repCount > 0 && (
                      <p>
                        También se eliminará{item.repCount > 1 ? "n" : ""}{" "}
                        <b>
                          {item.repCount} representante
                          {item.repCount > 1 ? "s" : ""}
                        </b>{" "}
                        de este propietario.
                      </p>
                    )}
                    <p>
                      Esta acción <b>NO</b> es reversible.{" "}
                    </p>
                  </>
                }
                open={openTemplatePopConfirm === item.key}
                onConfirm={() => handleDelete(item)}
                onCancel={() => setOpenTemplatePopConfirm(null)}
                onOpenChange={(open) =>
                  setOpenTemplatePopConfirm(open ? item.key : null)
                }
              >
                <CloseOutlined
                  className="tag-close-icon cursor-pointer"
                  onClick={() => setOpenTemplatePopConfirm(item.key)}
                />
              </Popconfirm>
            </Space>
          </Tag>
        ))}
      </Flex>
    </div>
  );
};
